import React from 'react';
import ColorPicker from './ColorPicker';
import { LOADER_TYPES, getLoaderMetadata } from '../../../data/loaderTypes';

const LoaderControls = ({ config, onChange }) => {
  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };

  // Reusable Plus/Minus button component for numeric controls
  const renderPlusMinusControl = (label, value, min, max, onChangeHandler) => {
    const currentValue = value || min;
    const isMin = currentValue <= min;
    const isMax = currentValue >= max;

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}: {currentValue}
        </label>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (!isMin) {
                onChangeHandler(currentValue - 1);
              }
            }}
            disabled={isMin}
            className={`
              px-4 py-2 rounded-lg font-medium transition-all text-lg
              ${isMin
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
            `}
            aria-label="Decrease"
          >
            −
          </button>
          <span className="flex-1 text-center font-semibold text-gray-900 dark:text-white min-w-[3rem]">
            {currentValue}
          </span>
          <button
            onClick={() => {
              if (!isMax) {
                onChangeHandler(currentValue + 1);
              }
            }}
            disabled={isMax}
            className={`
              px-4 py-2 rounded-lg font-medium transition-all text-lg
              ${isMax
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }
            `}
            aria-label="Increase"
          >
            +
          </button>
        </div>
      </div>
    );
  };

  const renderStyleSelector = () => {
    const styles = Object.values(LOADER_TYPES);
    
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Loader Style
        </label>
        <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
          {styles.map((style) => {
            const metadata = getLoaderMetadata(style);
            return (
              <button
                key={style}
                onClick={() => {
                  // Merge default config for new style
                  const defaultConfig = metadata.defaultConfig;
                  onChange({ ...config, ...defaultConfig, style });
                }}
                className={`
                  px-3 py-2.5 rounded-lg text-xs font-medium transition-all
                  text-center min-h-[48px] flex items-center justify-center
                  break-words hyphens-auto
                  ${
                    config.style === style
                      ? 'bg-gradient-primary text-white shadow-md'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
                title={metadata.description}
              >
                <span className="leading-tight px-1">{metadata.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCommonControls = () => {
    return (
      <>
        {/* Size - Always show for all loaders */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Size: {config.size || 50}px
          </label>
          <input
            type="range"
            min="20"
            max="100"
            value={config.size || 50}
            onChange={(e) => handleChange('size', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Speed */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Animation Speed: {config.speed}s
          </label>
          <input
            type="range"
            min="0.3"
            max="3"
            step="0.1"
            value={config.speed}
            onChange={(e) => handleChange('speed', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
      </>
    );
  };

  const renderDotsControls = () => {
    return (
      <>
        {/* Dot Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Dot Count',
          config.dotCount,
          3,
          8,
          (value) => handleChange('dotCount', value)
        )}

        {/* Min Dot Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Min Dot Size: {config.minDotSize || 8}px
          </label>
          <input
            type="range"
            min="4"
            max="20"
            value={config.minDotSize || 8}
            onChange={(e) => handleChange('minDotSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Max Dot Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Dot Size: {config.maxDotSize || 20}px
          </label>
          <input
            type="range"
            min="12"
            max="40"
            value={config.maxDotSize || 20}
            onChange={(e) => handleChange('maxDotSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Use Gradient */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>

        {/* Colors */}
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderPulseControls = () => {
    return (
      <>
        {/* Min Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Min Size: {config.minSize || 30}px
          </label>
          <input
            type="range"
            min="20"
            max="60"
            value={config.minSize || 30}
            onChange={(e) => handleChange('minSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Max Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Size: {config.maxSize || 50}px
          </label>
          <input
            type="range"
            min="30"
            max="100"
            value={config.maxSize || 50}
            onChange={(e) => handleChange('maxSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Use Gradient */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>

        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderRingControls = () => {
    return (
      <>
        {/* Stroke Width */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stroke Width: {config.strokeWidth || 4}px
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={config.strokeWidth || 4}
            onChange={(e) => handleChange('strokeWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>

        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        <ColorPicker
          color={config.secondaryColor}
          onChange={(color) => handleChange('secondaryColor', color)}
          label="Secondary Color"
        />
      </>
    );
  };

  const renderBarsControls = () => {
    return (
      <>
        {/* Bar Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Bar Count',
          config.barCount,
          3,
          8,
          (value) => handleChange('barCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bar Width: {config.barWidth || 4}px
          </label>
          <input
            type="range"
            min="2"
            max="10"
            value={config.barWidth || 4}
            onChange={(e) => handleChange('barWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bar Height: {config.barHeight || 30}px
          </label>
          <input
            type="range"
            min="10"
            max="50"
            value={config.barHeight || 30}
            onChange={(e) => handleChange('barHeight', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bar Spacing: {config.barSpacing || 4}px
          </label>
          <input
            type="range"
            min="2"
            max="10"
            value={config.barSpacing || 4}
            onChange={(e) => handleChange('barSpacing', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderWaveControls = () => {
    return (
      <>
        {/* Wave Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Wave Count',
          config.waveCount,
          3,
          8,
          (value) => handleChange('waveCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Wave Height: {config.waveHeight || 20}px
          </label>
          <input
            type="range"
            min="10"
            max="40"
            value={config.waveHeight || 20}
            onChange={(e) => handleChange('waveHeight', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Wave Width: {config.waveWidth || 6}px
          </label>
          <input
            type="range"
            min="3"
            max="12"
            value={config.waveWidth || 6}
            onChange={(e) => handleChange('waveWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderSpinnerControls = () => {
    return (
      <>
        {/* Arm Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Arm Count',
          config.armCount,
          4,
          12,
          (value) => handleChange('armCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Arm Length: {config.armLength || 20}px
          </label>
          <input
            type="range"
            min="10"
            max="30"
            value={config.armLength || 20}
            onChange={(e) => handleChange('armLength', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderProgressRingControls = () => {
    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Progress: {Math.round((config.progress || 0.7) * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={config.progress || 0.7}
            onChange={(e) => handleChange('progress', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stroke Width: {config.strokeWidth || 4}px
          </label>
          <input
            type="range"
            min="2"
            max="10"
            value={config.strokeWidth || 4}
            onChange={(e) => handleChange('strokeWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        <ColorPicker
          color={config.secondaryColor}
          onChange={(color) => handleChange('secondaryColor', color)}
          label="Secondary Color"
        />
      </>
    );
  };

  const renderSkeletonControls = () => {
    return (
      <>
        {/* Line Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Line Count',
          config.skeletonLineCount,
          1,
          6,
          (value) => handleChange('skeletonLineCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Line Height: {config.skeletonLineHeight || 12}px
          </label>
          <input
            type="range"
            min="8"
            max="20"
            value={config.skeletonLineHeight || 12}
            onChange={(e) => handleChange('skeletonLineHeight', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Line Spacing: {config.skeletonLineSpacing || 8}px
          </label>
          <input
            type="range"
            min="4"
            max="16"
            value={config.skeletonLineSpacing || 8}
            onChange={(e) => handleChange('skeletonLineSpacing', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Base Color"
        />
      </>
    );
  };

  const renderBounceControls = () => {
    return (
      <>
        {/* Dot Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Dot Count',
          config.dotCount,
          3,
          8,
          (value) => handleChange('dotCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Dot Size: {config.dotSize || 12}px
          </label>
          <input
            type="range"
            min="8"
            max="20"
            value={config.dotSize || 12}
            onChange={(e) => handleChange('dotSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Bounce Height: {config.bounceHeight || 20}px
          </label>
          <input
            type="range"
            min="10"
            max="40"
            value={config.bounceHeight || 20}
            onChange={(e) => handleChange('bounceHeight', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderRotateControls = () => {
    return (
      <>
        {/* Element Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Element Count',
          config.elementCount,
          4,
          12,
          (value) => handleChange('elementCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Element Size: {config.elementSize || 8}px
          </label>
          <input
            type="range"
            min="4"
            max="16"
            value={config.elementSize || 8}
            onChange={(e) => handleChange('elementSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderFadeControls = () => {
    return (
      <>
        {/* Element Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Element Count',
          config.elementCount,
          3,
          8,
          (value) => handleChange('elementCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Element Size: {config.elementSize || 12}px
          </label>
          <input
            type="range"
            min="8"
            max="20"
            value={config.elementSize || 12}
            onChange={(e) => handleChange('elementSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderScaleControls = () => {
    return (
      <>
        {/* Element Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Element Count',
          config.elementCount,
          2,
          6,
          (value) => handleChange('elementCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Min Scale: {config.minScale || 0.5}
          </label>
          <input
            type="range"
            min="0.1"
            max="0.9"
            step="0.1"
            value={config.minScale || 0.5}
            onChange={(e) => handleChange('minScale', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Scale: {config.maxScale || 1.0}
          </label>
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.1"
            value={config.maxScale || 1.0}
            onChange={(e) => handleChange('maxScale', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderRippleControls = () => {
    return (
      <>
        {/* Ripple Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Ripple Count',
          config.rippleCount,
          2,
          5,
          (value) => handleChange('rippleCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ripple Width: {config.rippleWidth || 2}px
          </label>
          <input
            type="range"
            min="1"
            max="5"
            value={config.rippleWidth || 2}
            onChange={(e) => handleChange('rippleWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderOrbitControls = () => {
    return (
      <>
        {/* Orbit Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Orbit Count',
          config.orbitCount,
          2,
          6,
          (value) => handleChange('orbitCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Orbit Size: {config.orbitSize || 8}px
          </label>
          <input
            type="range"
            min="4"
            max="16"
            value={config.orbitSize || 8}
            onChange={(e) => handleChange('orbitSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Orbit Radius: {config.orbitRadius || 20}px
          </label>
          <input
            type="range"
            min="10"
            max="30"
            value={config.orbitRadius || 20}
            onChange={(e) => handleChange('orbitRadius', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderGridControls = () => {
    return (
      <>
        {/* Grid Rows - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Grid Rows',
          config.gridRows,
          2,
          5,
          (value) => handleChange('gridRows', value)
        )}
        {/* Grid Columns - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Grid Columns',
          config.gridCols,
          2,
          5,
          (value) => handleChange('gridCols', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Cell Size: {config.cellSize || 10}px
          </label>
          <input
            type="range"
            min="6"
            max="20"
            value={config.cellSize || 10}
            onChange={(e) => handleChange('cellSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderLinesControls = () => {
    return (
      <>
        {/* Line Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Line Count',
          config.lineCount,
          3,
          8,
          (value) => handleChange('lineCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Line Width: {config.lineWidth || 3}px
          </label>
          <input
            type="range"
            min="2"
            max="6"
            value={config.lineWidth || 3}
            onChange={(e) => handleChange('lineWidth', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Line Length: {config.lineLength || 30}px
          </label>
          <input
            type="range"
            min="20"
            max="50"
            value={config.lineLength || 30}
            onChange={(e) => handleChange('lineLength', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderShimmerControls = () => {
    return (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Width: {config.width || 200}px
          </label>
          <input
            type="range"
            min="100"
            max="300"
            value={config.width || 200}
            onChange={(e) => handleChange('width', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Height: {config.height || 20}px
          </label>
          <input
            type="range"
            min="10"
            max="40"
            value={config.height || 20}
            onChange={(e) => handleChange('height', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Base Color"
        />
        <ColorPicker
          color={config.shimmerColor || '#ffffff'}
          onChange={(color) => handleChange('shimmerColor', color)}
          label="Shimmer Color"
        />
      </>
    );
  };

  const renderStaggerControls = () => {
    return (
      <>
        {/* Element Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Element Count',
          config.elementCount,
          3,
          8,
          (value) => handleChange('elementCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Element Size: {config.elementSize || 10}px
          </label>
          <input
            type="range"
            min="6"
            max="20"
            value={config.elementSize || 10}
            onChange={(e) => handleChange('elementSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stagger Delay: {config.staggerDelay || 0.1}s
          </label>
          <input
            type="range"
            min="0.05"
            max="0.3"
            step="0.05"
            value={config.staggerDelay || 0.1}
            onChange={(e) => handleChange('staggerDelay', parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderMorphControls = () => {
    return (
      <>
        {/* Shape Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Shape Count',
          config.shapeCount,
          3,
          6,
          (value) => handleChange('shapeCount', value)
        )}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderParticleControls = () => {
    return (
      <>
        {/* Particle Count - Plus/Minus buttons */}
        {renderPlusMinusControl(
          'Particle Count',
          config.particleCount,
          6,
          20,
          (value) => handleChange('particleCount', value)
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Particle Size: {config.particleSize || 4}px
          </label>
          <input
            type="range"
            min="2"
            max="8"
            value={config.particleSize || 4}
            onChange={(e) => handleChange('particleSize', parseInt(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.useGradient || false}
              onChange={(e) => handleChange('useGradient', e.target.checked)}
              className="rounded"
            />
            Use Gradient
          </label>
        </div>
        <ColorPicker
          color={config.primaryColor}
          onChange={(color) => handleChange('primaryColor', color)}
          label="Primary Color"
        />
        {config.useGradient && (
          <ColorPicker
            color={config.secondaryColor}
            onChange={(color) => handleChange('secondaryColor', color)}
            label="Secondary Color"
          />
        )}
      </>
    );
  };

  const renderLoaderSpecificControls = () => {
    switch (config.style) {
      case LOADER_TYPES.DOTS:
        return renderDotsControls();
      case LOADER_TYPES.PULSE:
        return renderPulseControls();
      case LOADER_TYPES.RING:
        return renderRingControls();
      case LOADER_TYPES.BARS:
        return renderBarsControls();
      case LOADER_TYPES.WAVE:
        return renderWaveControls();
      case LOADER_TYPES.SPINNER:
        return renderSpinnerControls();
      case LOADER_TYPES.PROGRESS_RING:
        return renderProgressRingControls();
      case LOADER_TYPES.SKELETON:
        return renderSkeletonControls();
      case LOADER_TYPES.BOUNCE:
        return renderBounceControls();
      case LOADER_TYPES.ROTATE:
        return renderRotateControls();
      case LOADER_TYPES.FADE:
        return renderFadeControls();
      case LOADER_TYPES.SCALE:
        return renderScaleControls();
      case LOADER_TYPES.RIPPLE:
        return renderRippleControls();
      case LOADER_TYPES.ORBIT:
        return renderOrbitControls();
      case LOADER_TYPES.GRID:
        return renderGridControls();
      case LOADER_TYPES.LINES:
        return renderLinesControls();
      case LOADER_TYPES.SHIMMER:
        return renderShimmerControls();
      case LOADER_TYPES.STAGGER:
        return renderStaggerControls();
      case LOADER_TYPES.MORPH:
        return renderMorphControls();
      case LOADER_TYPES.PARTICLE:
        return renderParticleControls();
      default:
        return (
          <ColorPicker
            color={config.primaryColor}
            onChange={(color) => handleChange('primaryColor', color)}
            label="Primary Color"
          />
        );
    }
  };

  return (
    <div className="space-y-6 p-4">
      {renderStyleSelector()}
      {renderCommonControls()}
      {renderLoaderSpecificControls()}

      {/* Background Options */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.showBackground || false}
            onChange={(e) => handleChange('showBackground', e.target.checked)}
            className="rounded"
          />
          Show Background Dimming
        </label>
        {config.showBackground && (
          <div className="mt-2">
            <label className="block text-xs text-gray-400 mb-1">
              Opacity: {config.backgroundOpacity || 0.3}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={config.backgroundOpacity || 0.3}
              onChange={(e) => handleChange('backgroundOpacity', parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Helper Classes Toggle */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={config.includeHelperClasses !== false}
            onChange={(e) => handleChange('includeHelperClasses', e.target.checked)}
            className="rounded"
          />
          Include Helper Classes (Color Extension)
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Uncheck if you already have Color extension helper classes in your project
        </p>
      </div>
    </div>
  );
};

export default LoaderControls;
